import React from 'react';
import { brandModels } from '../data/mockData';
interface SidebarProps {
  activeBrand: string;
  setActiveBrand: (brand: string) => void;
  activeModel: string;
  setActiveModel: (model: string) => void;
  activeType: string | null;
  setActiveType: (type: string | null) => void;
  activeYears: string[];
  setActiveYears: React.Dispatch<React.SetStateAction<string[]>>;
  activeMarkets: string[];
  setActiveMarkets: React.Dispatch<React.SetStateAction<string[]>>;
}
export function Sidebar({
  activeBrand,
  setActiveBrand,
  activeModel,
  setActiveModel,
  activeType,
  setActiveType,
  activeYears,
  setActiveYears,
  activeMarkets,
  setActiveMarkets
}: SidebarProps) {
  const toggleMultiSelect = (item: string, state: string[], setState: React.Dispatch<React.SetStateAction<string[]>>) => {
    if (state.includes(item)) {
      setState(state.filter(i => i !== item));
    } else {
      setState([...state, item]);
    }
  };
  const handleBrandChange = (brand: string) => {
    setActiveBrand(brand);
    // Reset model to the first model of the new brand
    const models = brandModels[brand];
    if (models && models.length > 0) {
      setActiveModel(models[0]);
    }
  };
  const handleReset = () => {
    setActiveBrand('Toyota');
    setActiveModel('Yaris Cross');
    setActiveType('Tactical');
    setActiveYears([]);
    setActiveMarkets([]);
  };
  const availableModels = brandModels[activeBrand] || [];
  // Calculate active filters count
  const activeFiltersCount = 1 +
  // Brand is always 1
  1 + (
  // Model is always 1
  activeType ? 1 : 0) + activeYears.length + activeMarkets.length;
  const RequiredBadge = () => <span className="text-[10px] uppercase font-bold tracking-wider bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 rounded-md" data-id="element-99">
      Required
    </span>;
  const OptionalBadge = () => <span className="text-xs italic text-muted-foreground" data-id="element-100">Optional</span>;
  return <aside className="w-full lg:w-[320px] shrink-0" data-id="element-101">
      <div className="sticky top-6 bg-card rounded-2xl border border-border shadow-sm p-6 flex flex-col gap-6" data-id="element-102">
        {/* Header */}
        <div className="flex items-center justify-between pb-2" data-id="element-103">
          <h2 className="text-xl font-bold text-foreground" data-id="element-104">Filtri</h2>
          <button onClick={handleReset} className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-id="element-105">
            Reset
          </button>
        </div>

        {/* 1. Brand */}
        <div className="flex flex-col gap-3" data-id="element-106">
          <div className="flex items-center justify-between" data-id="element-107">
            <span className="text-sm font-semibold text-foreground" data-id="element-108">Brand</span>
            <RequiredBadge data-id="element-109" />
          </div>
          <div className="flex gap-2" data-id="element-110">
            {['Toyota', 'Lexus'].map(brand => <button key={brand} onClick={() => handleBrandChange(brand)} className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-colors ${activeBrand === brand ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`} data-id="element-111">
                {brand}
              </button>)}
          </div>
        </div>

        {/* 2. Model */}
        <div className="flex flex-col gap-3 border-t border-border pt-6" data-id="element-112">
          <div className="flex items-center justify-between" data-id="element-113">
            <span className="text-sm font-semibold text-foreground" data-id="element-114">Model</span>
            <RequiredBadge data-id="element-115" />
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-116">
            {availableModels.map(model => <button key={model} onClick={() => setActiveModel(model)} className={`py-2 px-4 rounded-full text-sm font-medium transition-colors ${activeModel === model ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`} data-id="element-117">
                {model}
              </button>)}
          </div>
        </div>

        {/* 3. Campaign Type */}
        <div className="flex flex-col gap-3 border-t border-border pt-6" data-id="element-118">
          <div className="flex items-center justify-between" data-id="element-119">
            <span className="text-sm font-semibold text-foreground" data-id="element-120">
              Campaign type
            </span>
            <RequiredBadge data-id="element-121" />
          </div>
          <div className="flex gap-2" data-id="element-122">
            <button onClick={() => setActiveType(activeType === 'Brand' ? null : 'Brand')} className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold transition-all ${activeType === 'Brand' ? 'bg-slate-800 text-white shadow-md' : 'bg-slate-800/10 text-slate-800 hover:bg-slate-800/20'}`} data-id="element-123">
              Brand
            </button>
            <button onClick={() => setActiveType(activeType === 'Tactical' ? null : 'Tactical')} className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold transition-all ${activeType === 'Tactical' ? 'bg-red-500 text-white shadow-md' : 'bg-red-500/10 text-red-600 hover:bg-red-500/20'}`} data-id="element-124">
              Tactical
            </button>
          </div>
        </div>

        {/* 4. Year */}
        <div className="flex flex-col gap-3 border-t border-border pt-6" data-id="element-125">
          <div className="flex items-center justify-between" data-id="element-126">
            <span className="text-sm font-semibold text-foreground" data-id="element-127">Year</span>
            <OptionalBadge data-id="element-128" />
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-129">
            {['2023', '2024', '2025', '2026'].map(year => <button key={year} onClick={() => toggleMultiSelect(year, activeYears, setActiveYears)} className={`py-2 px-4 rounded-full text-sm font-medium transition-colors ${activeYears.includes(year) ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`} data-id="element-130">
                {year}
              </button>)}
          </div>
        </div>

        {/* 5. Market */}
        <div className="flex flex-col gap-3 border-t border-border pt-6" data-id="element-131">
          <div className="flex items-center justify-between" data-id="element-132">
            <span className="text-sm font-semibold text-foreground" data-id="element-133">
              Market
            </span>
            <OptionalBadge data-id="element-134" />
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-135">
            {['SLO', 'CRO', 'RS', 'BiH', 'MNE'].map(market => <button key={market} onClick={() => toggleMultiSelect(market, activeMarkets, setActiveMarkets)} className={`py-2 px-4 rounded-full text-sm font-medium transition-colors ${activeMarkets.includes(market) ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`} data-id="element-136">
                {market}
              </button>)}
          </div>
        </div>

        {/* Footer Summary */}
        <div className="mt-2 pt-4 border-t border-border flex items-center justify-between text-sm" data-id="element-137">
          <span className="text-muted-foreground" data-id="element-138">Active filters</span>
          <span className="font-semibold bg-primary/10 text-primary px-2.5 py-0.5 rounded-full" data-id="element-139">
            {activeFiltersCount}
          </span>
        </div>
      </div>
    </aside>;
}